/**
 * HUD, combo callouts, title, pause, RESULT screen. DOM + canvas.
 *
 * OWNER: `ui` — ARCHITECTURE.md §3.
 *
 * DESIGN RULES THIS FILE OBEYS, AND WHY
 * -------------------------------------
 * · The HUD is authored in a fixed 1920x1080 design space. `onResize` sizes a single root
 *   element to `viewport / scale` and applies one CSS transform, so corner-anchored layers
 *   stay in the real corners at any aspect ratio while type keeps its designed size.
 * · NOTHING uses a CSS transition, CSS animation or wall clock. Every animated quantity is
 *   a pure function of `ctx.time.sim`, so `freezeClocks()` makes a shot bit-reproducible
 *   (ARCHITECTURE.md §7.3). `update()` may be called any number of times per sim step and
 *   the pixels will not change.
 * · `update(dt, alpha)` is presentation only. It reads `ctx.gameplay` / `ctx.player`;
 *   it never writes to them.
 * · No per-frame allocation in the hot path: layers are pre-built, lists are pooled
 *   (`ActiveList`), number formatting is memoised, canvases redraw only when their content
 *   signature changes.
 */

import { Vector3 } from 'three';
import { ActiveList } from '../core/pool.js';
import { clamp, clamp01, ease } from '../core/mathx.js';
import TUNING from './tuning.js';
import { C, alpha, resetFamilies } from './theme.js';
import {
  drawTimer, drawTimeBonuses, drawPlate, drawCallouts, drawCard, drawDrive,
  drawMeter, drawChevron, fmtTime, fmtInt, fmtMoney, resetThumbCache,
} from './hud.js';
import { drawResult } from './result.js';
import { drawTitle, drawCountdown, drawTimeUp, drawPause } from './overlays.js';

const T = TUNING;
const DES_W = T.design.width;
const DES_H = T.design.height;

/** Landmark names used only while `world`/`gameplay` have not published a real target. */
const PREVIEW_LANDMARKS = [
  'Dépanneur Chez Momo', 'Rue Saint-Denis', 'Avenue du Mont-Royal',
  'Ruelle Saint-Dominique', 'Parc Laurier', 'Boulevard Saint-Laurent',
];

export class UiSystem {
  static id = 'ui';

  constructor(ctx) {
    this.ctx = ctx;
    this.id = 'ui';

    this.root = null;
    this.layers = new Map();
    this.scale = 1;
    this.viewW = DES_W;
    this.viewH = DES_H;
    this.q = 1;
    this.frozen = false;

    /** Harness-forced values. Sticky until reset(). See applyHarnessState. */
    this.forced = Object.create(null);

    this.rng = null;

    this.callouts = new ActiveList(T.pools.callouts, () => ({
      text: '', style: 'combo', at: 0, life: 0, sticky: false, combo: 0, mult: 1,
    }));
    this.bonuses = new ActiveList(T.pools.bonuses, () => ({ seconds: 0, at: 0, sticky: false }));

    this.model = {
      visible: true, phase: 'playing', weather: 'thaw', seed: 1,
      timeRemaining: 60, score: 0, cash: 0, combo: 0, multiplier: 1,
      repaired: 0, total: 0, speedKmh: 0, gear: 'D', countdown: 3,
      cashShown: 0, scoreShown: 0,
      target: { valid: false, name: '', distance: 0, onScreen: false, edgeAngle: 0, x: 0, y: 0 },
      /**
       * The 12x3 coverage mosaic is gone (round 8: "the denser one in the bottom centre …
       * hard to parse at a glance"), and with it the per-frame resample of gameplay's
       * `cells` array that fed it. REFERENCE §5.1 measures PowerWash's completion HUD as a
       * badge, a name, a thin segmented bar and a star row — no cell grid.
       */
      repair: { active: false, shown: 0, quality: 0.8, name: 'Nid-de-poule', snapAt: -1, fade: 0 },
      result: { rank: 'B', perfects: 0, bestCombo: 0, timeBonus: 0, score: 0, cash: 0, repaired: 0, total: 0 },
      /**
       * `mission-complete` rubric item 8 — the named street, in plain text at the top of the
       * RESULT panel. Latched ONCE when the phase becomes 'result' (see `_latchPlace`) rather
       * than sampled per frame, so the panel cannot change its own street name while it is on
       * screen and the shot stays bit-reproducible.
       */
      street: '', nextSite: '',
      chips: [
        { label: 'MEILLEUR COMBO', value: '×0' },
        { label: 'PARFAITS', value: '0' },
        { label: 'TEMPS BONUS', value: '+0' },
      ],
    };

    // Odometers — {shown, from, to, at}
    this._odoCash = { shown: 0, from: 0, to: 0, at: 0 };
    this._odoScore = { shown: 0, from: 0, to: 0, at: 0 };

    // Repair meter smoothing origin. `_fillDone` tracks whether the latched target was the
    // completed value, so crossing into 100 % always re-latches even if the last increment was
    // below the re-latch threshold.
    this._fillFrom = 0; this._fillTo = -1; this._fillAt = 0; this._fillDone = false;
    this._repairOnAt = -999; this._repairOffAt = -999;
    /** True while a harness-forced repair session's fade origin must not be re-stamped. */
    this._repairPinned = false;

    this._vigDamage = { strength: 0, at: -999, decay: 0.6 };
    this._paused = false;
    this._pausePrev = false;
    this._pauseAt = 0;
    this._phaseAt = 0;
    this._resultAt = 0;
    this._titleAt = 0;

    this._offs = [];
    this._sig = new Float64Array(10).fill(NaN);
    this._forceRedraw = true;
    this._previewTarget = null;

    this._v = new Vector3();
    this._v2 = new Vector3();

    /** Last values the RESULT stat chips were formatted from — see `_syncResult`. */
    this._chipSrc = [NaN, NaN, NaN];
  }

  // ------------------------------------------------------------------ init

  async init() {
    const ctx = this.ctx;
    this.rng = ctx.rng.fork('ui');

    this.model.seed = (ctx.seed | 0) || 1;
    this._previewTarget = {
      name: this.rng.pick(PREVIEW_LANDMARKS),
      position: new Vector3(6.5, 0.2, 62),
      distance: 62,
    };

    this._buildDom();
    this._bindEvents();

    // System fonts resolve synchronously, but wait anyway so the first shot never races a
    // font swap. ARCHITECTURE.md §7.3.
    if (document.fonts && document.fonts.ready) ctx.assets.register(document.fonts.ready);

    const vp = ctx.viewport;
    this.onResize(vp.width || DES_W, vp.height || DES_H);

    // Published interface (replaces the null-object default, ARCHITECTURE.md §4.10).
    const self = this;
    ctx.ui = {
      announce: (text, style, durationMs) => self._pushCallout(text, style || 'combo', durationMs),
      setHudVisible: (v) => { self.forced.hudVisible = !!v; self._forceRedraw = true; },
      flashDamage: (strength) =>
        self._flash(strength != null ? strength : T.vignette.thumpStrength, T.vignette.thumpDecay),
      timeBonus: (seconds) => self._pushBonus(seconds, false),
      model: self.model,
      designSize: { width: DES_W, height: DES_H },
      get visible() { return self.model.visible; },
      get paused() { return self._paused; },
    };
  }

  // ------------------------------------------------------------------ DOM

  _buildDom() {
    let host = document.getElementById('hud');
    if (!host) {
      host = document.createElement('div');
      host.id = 'hud';
      host.style.cssText = 'position:fixed;inset:0;pointer-events:none;';
      document.body.appendChild(host);
    }
    this.host = host;
    while (host.firstChild) host.removeChild(host.firstChild);

    const root = document.createElement('div');
    root.id = 'ppm-ui';
    root.style.cssText =
      'position:absolute;left:0;top:0;transform-origin:0 0;pointer-events:none;contain:layout style;';
    host.appendChild(root);
    this.root = root;

    // Full-frame scrim behind the overlay canvas (title / result letterbox fill).
    this.scrim = document.createElement('div');
    this.scrim.style.cssText = 'position:absolute;left:0;top:0;display:none;background:#0B0D10;';
    root.appendChild(this.scrim);

    // Damage vignette + critical-clock vignette. Opacity only — never a CSS transition.
    this.vigDamage = document.createElement('div');
    this.vigDamage.style.cssText =
      'position:absolute;left:0;top:0;display:none;opacity:0;background:radial-gradient(ellipse at 50% 52%,' +
      `${alpha(C.hotRed, 0)} 34%, ${alpha(C.hotRed, 0.55)} 74%, ${alpha(C.deepRed, 0.95)} 100%);`;
    root.appendChild(this.vigDamage);

    this.vigClock = document.createElement('div');
    this.vigClock.style.cssText =
      'position:absolute;left:0;top:0;display:none;opacity:0;background:radial-gradient(ellipse at 50% 52%,' +
      `${alpha(C.deepRed, 0)} 46%, ${alpha(C.deepRed, 0.72)} 100%);`;
    root.appendChild(this.vigClock);

    const mk = (name, box, anchor) => {
      const el = document.createElement('canvas');
      el.style.cssText = 'position:absolute;left:0;top:0;';
      root.appendChild(el);
      this.layers.set(name, { name, el, g: el.getContext('2d', { alpha: true }), box, anchor });
    };

    mk('timer', T.timer.box, 'tl');
    // The bonus token is its own layer so a flying "+10 SEC" does not force the (much larger)
    // timer canvas to repaint every frame. ui.update p95 budget, ARCHITECTURE.md §9.
    mk('bonus', T.timeBonus.box, 'tl');
    mk('plate', T.plate.box, 'tr');
    mk('callout', T.callout.box, 'tr');
    mk('card', T.card.box, 'tl');
    mk('drive', T.drive.box, 'br');
    // Bottom-LEFT, not bottom-centre. §2.0: "the four corners are used and the middle is left
    // clear for the road." Round 8 flagged the repair widget for parking a dense progress
    // read across the bottom centre of `pothole-fill`, arguing with the run progress in the
    // opposite corner. Two corners, two different questions.
    mk('meter', T.meter.box, 'bl');
    mk('chevron', { x: 0, y: 0, w: T.chevron.size, h: T.chevron.size }, 'free');
    mk('overlay', { x: 0, y: 0, w: DES_W, h: DES_H }, 'cc');
  }

  onResize(width, height) {
    if (!this.root) return;
    const w = Math.max(2, width), h = Math.max(2, height);
    const scale = Math.min(w / DES_W, h / DES_H);
    this.scale = scale;
    this.viewW = w / scale;
    this.viewH = h / scale;

    const dpr = (this.ctx.viewport && this.ctx.viewport.dpr) || 1;
    this.q = clamp(Math.round(scale * dpr * 4) / 4, 1, T.maxCanvasScale);

    this.root.style.width = this.viewW + 'px';
    this.root.style.height = this.viewH + 'px';
    this.root.style.transform = `scale(${scale})`;

    for (const el of [this.scrim, this.vigDamage, this.vigClock]) {
      el.style.width = this.viewW + 'px';
      el.style.height = this.viewH + 'px';
    }

    for (const layer of this.layers.values()) this._placeLayer(layer);
    this._forceRedraw = true;
    this._sig.fill(NaN);
  }

  _placeLayer(layer) {
    const b = layer.box;
    if (layer.anchor === 'free') { this._sizeCanvas(layer, b.w, b.h); return; }
    let left = b.x, top = b.y;
    switch (layer.anchor) {
      case 'tr': left = this.viewW - (DES_W - (b.x + b.w)) - b.w; break;
      case 'br':
        left = this.viewW - (DES_W - (b.x + b.w)) - b.w;
        top = this.viewH - (DES_H - (b.y + b.h)) - b.h;
        break;
      case 'bl': top = this.viewH - (DES_H - (b.y + b.h)) - b.h; break;
      case 'bc':
        left = (this.viewW - b.w) * 0.5;
        top = this.viewH - (DES_H - (b.y + b.h)) - b.h;
        break;
      case 'cc':
        left = (this.viewW - b.w) * 0.5;
        top = (this.viewH - b.h) * 0.5;
        break;
      default: break;
    }
    layer.el.style.left = left + 'px';
    layer.el.style.top = top + 'px';
    this._sizeCanvas(layer, b.w, b.h);
  }

  _sizeCanvas(layer, w, h) {
    layer.el.style.width = w + 'px';
    layer.el.style.height = h + 'px';
    const bw = Math.round(w * this.q), bh = Math.round(h * this.q);
    if (layer.el.width !== bw || layer.el.height !== bh) {
      layer.el.width = bw;
      layer.el.height = bh;
    }
    layer.g.setTransform(this.q, 0, 0, this.q, 0, 0);
  }

  // ------------------------------------------------------------------ events

  _bindEvents() {
    const bus = this.ctx.bus;
    const on = (type, fn) => this._offs.push(bus.on(type, fn));

    on('game:started', () => this._resetRun());
    on('game:phaseChanged', (p) => this._onPhase(p && p.to));
    on('game:timeExtended', (p) => this._pushBonus(p && p.seconds, false));
    on('game:timeWarning', (p) => {
      if (p && p.secondsLeft === 10) this._pushCallout('DERNIER TOUR!', 'bad', 1400);
    });
    on('game:over', (p) => {
      const r = this.model.result;
      if (p) {
        if (p.score != null) r.score = p.score;
        if (p.cash != null) r.cash = p.cash;
        if (p.repaired != null) r.repaired = p.repaired;
        if (p.total != null) r.total = p.total;
        if (p.rank) r.rank = String(p.rank).toUpperCase();
        if (p.perfects != null) r.perfects = p.perfects;
        if (p.bestCombo != null) r.bestCombo = p.bestCombo;
      }
      this._onPhase('result');
    });

    on('repair:started', () => { this._repairOnAt = this.ctx.time.sim; });
    on('repair:milestone', (p) => {
      if (p && p.pct === 50) this._pushCallout('À MI-CHEMIN', 'info', 900);
    });
    on('repair:completed', (p) => {
      const perfect = !!(p && p.perfect);
      this._pushCallout(perfect ? this.rng.pick(T.lines.perfect) : this.rng.pick(T.lines.repair),
        perfect ? 'perfect' : 'combo', 1800);
      if (p && p.timeAwarded) this._pushBonus(p.timeAwarded, false);
      this.model.repair.snapAt = this.ctx.time.sim;
    });
    on('repair:failed', () => this._pushCallout('RATÉ', 'bad', 1200));

    on('combo:increased', (p) => {
      this._pushCallout(this.rng.pick(T.lines.combo), 'combo', 1800,
        p ? p.combo : 0, p ? p.multiplier : 1);
    });
    on('combo:tierReached', (p) => {
      this._pushCallout('EN FEU!', 'perfect', 2000, p ? p.combo : 0, 1);
    });
    on('combo:broken', () => {
      this._pushCallout(this.rng.pick(T.lines.break), 'bad', 1200);
      this._flash(T.vignette.comboBreakStrength, T.vignette.comboBreakDecay);
    });

    on('pothole:thumped', () => {
      this._pushCallout(this.rng.pick(T.lines.thump), 'bad', 1400);
      this._flash(T.vignette.thumpStrength, T.vignette.thumpDecay);
    });
    on('vehicle:collided', (p) => {
      if (!p) return;
      if (p.other !== 'cone' || (p.impulse || 0) > 6) {
        this._flash(T.vignette.crashStrength, T.vignette.crashDecay);
      }
    });

    // `gameplay` fires a WORLD-SPACE popText over the truck for every slalom chain link.
    // Raising a corner banner on the same event rendered the same phrase twice within 150 px
    // (night-rain). The world-space feedback wins — the player's eye is already there — so
    // the corner only speaks for a chain milestone, and never for the link that just popped.
    on('cone:slalomChained', (p) => {
      const chain = p ? p.chain : 0;
      if (chain > 0 && chain % T.callout.slalomBannerEvery === 0) {
        this._pushCallout(this.rng.pick(T.lines.slalom), 'combo', 1600,
          chain, (p && p.multiplier) || 1);
      }
    });
    on('cone:slalomBroken', () => this._pushCallout('SLALOM BRISÉ', 'bad', 900));

    on('ui:announce', (p) => {
      if (p && p.text) this._pushCallout(p.text, p.style || 'combo', p.durationMs);
    });
  }

  /**
   * Read the street the run finished on, and the street the next site is on, exactly once.
   *
   * `world.streetName` is a world-specific extra (world.js, "not part of §4.5"), so it is
   * feature-detected; `landmarkNear` is the §4.5 contract and returns `{kind:'street'}` when
   * nothing closer is around. Everything falls back to a literal from tuning, so the banner
   * can never render an empty line on a cold harness boot.
   */
  _latchPlace() {
    const m = this.model;
    const w = this.ctx.world;
    const p = this.ctx.player && this.ctx.player.position;
    let here = '';
    if (p && w) {
      if (typeof w.streetName === 'function') here = w.streetName(p.x, p.z) || '';
      if (!here && typeof w.landmarkNear === 'function') {
        const l = w.landmarkNear(p.x, p.z);
        if (l && l.name) here = l.name;
      }
    }
    const B = T.result.banner;
    m.street = (here ? String(here).toUpperCase() : '') || B.streetFallback;

    // Never repeat the banner's street in the teaser — that reads as a bug, not as a plan.
    const nx = m.target && m.target.valid && m.target.name ? String(m.target.name).toUpperCase() : '';
    let next = nx && nx !== m.street ? nx : '';
    for (let i = 0; !next && i < B.nextFallbacks.length; i++) {
      if (B.nextFallbacks[i] !== m.street) next = B.nextFallbacks[i];
    }
    m.nextSite = next;
  }

  _onPhase(to) {
    if (!to) return;
    const sim = this.ctx.time.sim;
    this._phaseAt = sim;
    if (to === 'result') { this._resultAt = sim; this._latchPlace(); }
    if (to === 'title') this._titleAt = sim;
    delete this.forced.phase;
    this.model.phase = to;
    this._forceRedraw = true;
  }

  _resetRun() {
    this.callouts.clear();
    this.bonuses.clear();
    this._odoCash.shown = 0; this._odoCash.from = 0; this._odoCash.to = 0; this._odoCash.at = 0;
    this._odoScore.shown = 0; this._odoScore.from = 0; this._odoScore.to = 0; this._odoScore.at = 0;
    this._vigDamage.strength = 0;
    this.model.street = '';
    this.model.nextSite = '';
    this._chipSrc[0] = NaN; this._chipSrc[1] = NaN; this._chipSrc[2] = NaN;
    this._forceRedraw = true;
  }

  // ------------------------------------------------------------------ callout / popup

  _pushCallout(text, style, durationMs, combo, mult) {
    if (!text) return;
    const sim = this.ctx.time.sim;
    const txt = String(text).toUpperCase();
    for (let i = 0; i < this.callouts.count; i++) {
      const c = this.callouts.items[i];
      if (c.text === txt && Math.abs(c.at - sim) < 1e-6) return;
    }
    const it = this._spawnCallout();
    if (!it) return;
    it.text = txt;
    it.style = style || 'combo';
    it.at = sim;
    it.life = durationMs ? durationMs / 1000 : T.callout.life;
    it.sticky = false;
    it.combo = combo || 0;
    it.mult = mult || 1;
    this._forceRedraw = true;
  }

  /** Pooled spawn that evicts the OLDEST NON-STICKY entry when the list is full. */
  _spawnCallout() {
    let it = this.callouts.spawn();
    if (it) return it;
    let oldest = -1, oldestAt = Infinity;
    for (let i = 0; i < this.callouts.count; i++) {
      const c = this.callouts.items[i];
      if (c.sticky) continue;
      if (c.at < oldestAt) { oldestAt = c.at; oldest = i; }
    }
    if (oldest < 0) return null;            // every slot is a pinned harness callout
    this.callouts.retire(oldest);
    return this.callouts.spawn();
  }

  _pushBonus(seconds, sticky) {
    if (!seconds) return;
    let it = this.bonuses.spawn();
    if (!it) {
      let oldest = -1, oldestAt = Infinity;
      for (let i = 0; i < this.bonuses.count; i++) {
        const b = this.bonuses.items[i];
        if (b.sticky) continue;
        if (b.at < oldestAt) { oldestAt = b.at; oldest = i; }
      }
      if (oldest < 0) return;
      this.bonuses.retire(oldest);
      it = this.bonuses.spawn();
      if (!it) return;
    }
    it.seconds = seconds;
    it.at = this.ctx.time.sim;
    it.sticky = !!sticky;
    this._forceRedraw = true;
  }

  _flash(strength, decay) {
    if (strength > this._vigDamage.strength ||
        this.ctx.time.sim - this._vigDamage.at > this._vigDamage.decay) {
      this._vigDamage.strength = strength;
    }
    this._vigDamage.at = this.ctx.time.sim;
    this._vigDamage.decay = decay;
  }

  /** `ui` is not in FIXED_ORDER (ARCHITECTURE.md §2) — present for interface parity only. */
  fixedUpdate() {}

  // ------------------------------------------------------------------ presentation

  update() {
    if (!this.root) return;
    const sim = this.ctx.time.sim;
    this._pollPause();
    this._syncModel(sim);
    this._draw(sim);
  }

  _pollPause() {
    const p = !!this.ctx.input.pause;
    if (p && !this._pausePrev) {
      this._paused = !this._paused;
      this._pauseAt = this.ctx.time.sim;
      this._forceRedraw = true;
    }
    this._pausePrev = p;
  }

  _f(key, fallback) {
    const v = this.forced[key];
    return v === undefined ? fallback : v;
  }

  _syncModel(sim) {
    const ctx = this.ctx;
    const gp = ctx.gameplay;
    const pl = ctx.player;
    const m = this.model;

    m.phase = this._f('phase', gp.phase || 'playing');
    m.visible = this._f('hudVisible', true) !== false;
    m.weather = this._f('weather', gp.weather || 'thaw');
    m.timeRemaining = this._f('timeRemaining', gp.timeRemaining || 0);
    m.combo = this._f('combo', gp.combo || 0);
    m.multiplier = this._f('multiplier', gp.multiplier || 1);
    m.repaired = this._f('repaired', gp.repaired || 0);
    m.total = this._f('total', gp.total || 0);
    m.speedKmh = pl.speedKmh || 0;
    m.gear = pl.gear || 'D';
    m.countdown = gp.countdown != null ? gp.countdown : Math.max(0, 3 - (sim - this._phaseAt));

    // Odometers — the tick-up is free dopamine, so nothing snaps unless the jump is tiny.
    m.cashShown = this._roll(this._odoCash, this._f('cash', gp.cash || 0), sim);
    m.scoreShown = this._roll(this._odoScore, this._f('score', gp.score || 0), sim);
    m.score = this._f('score', gp.score || 0);
    m.cash = this._f('cash', gp.cash || 0);

    this._syncRepair(sim, gp);
    this._syncTarget(gp, pl);
    this._syncResult(m);
  }

  _roll(o, target, sim) {
    if (target !== o.to) {
      o.from = Math.abs(target - o.to) < T.plate.rollMin ? target : o.shown;
      o.to = target;
      o.at = sim;
    }
    const t = ease.outQuint(clamp01((sim - o.at) / T.plate.rollSeconds));
    o.shown = o.from + (o.to - o.from) * t;
    return o.shown;
  }

  _syncRepair(sim, gp) {
    const r = this.model.repair;
    const s = this.forced.repairSession !== undefined ? this.forced.repairSession : gp.repairSession;
    const active = !!(s && s.active !== false && (s.fill01 != null || s.potholeId != null));

    if (active !== r.active) {
      if (active) {
        // BUG FIX: `applyHarnessState` back-dates `_repairOnAt` so a forced session is already
        // faded IN when the shot is taken. Re-stamping it here clobbered that, and because
        // `harness.step()` does not call `update()`, the very first (and only) presentation
        // pass saw fade = 0 — the per-repair meter drew at globalAlpha 0 and `pothole-fill`
        // captured with no meter on screen at all. Honour the pin exactly once.
        if (!this._repairPinned) this._repairOnAt = sim;
        this._repairPinned = false;
      } else {
        this._repairOffAt = sim;
      }
      r.active = active;
      this._forceRedraw = true;
    }
    r.fade = active
      ? clamp01((sim - this._repairOnAt) / T.meter.fade)
      : clamp01(1 - (sim - this._repairOffAt) / T.meter.fade);

    if (!active) return;

    /**
     * ------------------------------------------------------- THE READOUT MUST ACTUALLY CLIMB
     *
     * Round 11 game-feel adjudication, FATAL: "the one mechanic the game is named after has a
     * correct simulation with no readout." Measured on the REAL frame path with
     * `tools/ui-meter-check.mjs` (which drives `game.frame()` at 60 fps rather than
     * `step()`-then-`render()`, and is the reason no capture-based check ever caught this):
     * true fill climbed 0.066 → 0.955 while `shown` sat at **0.052 for 211 of 240 frames**,
     * then snapped to 1.0. Mean lag **42.9 pp**, max **94.8 pp**.
     *
     * Cause: the guard was `fill !== this._fillTo`, an exact float compare. The fill moves on
     * every fixed step of a repair, so `_fillAt = sim` was re-stamped every frame,
     * `(sim - _fillAt)` was always 0, and `shown` could only ever evaluate to `_fillFrom` —
     * which had just been set to the same stale `shown`. The ease could only advance on a
     * presentation frame where the simulation did NOT step, which at 60 Hz is almost never.
     *
     * ---- WHY THIS IS NOT AN EXPONENTIAL CHASE
     *
     * The obvious fix is `shown += (fill - shown) * (1 - exp(-dt/tau))`. That would break §7.3:
     * a dt-driven integrator in `update()` is presentation state whose value depends on HOW MANY
     * times `update()` ran, and the harness calls `step()` and `render()` independently. Shots
     * would stop being bit-reproducible. `shown` therefore stays a pure function of sim state,
     * and the fix is in two parts:
     *
     *   1. re-latch only when the target has moved by `relatchEps`, so `sim - _fillAt` actually
     *      accumulates and the ease has time to run; and
     *   2. bound the result absolutely. Part 1 alone leaves the steady-state lag proportional to
     *      the fill RATE (`eps / outCubic(eps / rate / smooth)`) — fine at the 0.28/s a normal
     *      repair runs at, but ~4.9 pp on a one-second repair. The clamp makes the 3 pp
     *      criterion hold by construction at any rate gameplay chooses.
     */
    const fill = clamp01(s.fill01 != null ? s.fill01 : 0);
    const done = fill >= 0.999;
    const first = this._fillTo < 0;
    if (first || done !== this._fillDone || Math.abs(fill - this._fillTo) >= T.meter.relatchEps) {
      this._fillFrom = first ? fill : r.shown;
      this._fillTo = fill;
      this._fillAt = sim;
      this._fillDone = done;
    }
    // Driven by the TRUE fill, not by the latch: a completion whose last increment fell under
    // `relatchEps` must still fire the 100 % snap.
    if (done) { if (r.snapAt < 0) r.snapAt = sim; } else r.snapAt = -1;

    const eased = this._fillFrom + (this._fillTo - this._fillFrom)
      * ease.outCubic(clamp01((sim - this._fillAt) / T.meter.smooth));
    const lo = fill - T.meter.maxLag, hi = fill + T.meter.maxLag;
    r.shown = eased < lo ? lo : eased > hi ? hi : eased;
    r.quality = s.quality01 != null ? s.quality01 : 0.8;
    r.name = s.name || (this.model.target.valid ? this.model.target.name : 'Nid-de-poule');
  }

  _syncTarget(gp, pl) {
    const t = this.model.target;
    let at = this.forced.activeTarget !== undefined ? this.forced.activeTarget : gp.activeTarget;

    // Scaffolding: while `gameplay` has not landed there is no objective to point at, but
    // the harness still has to compose and grade the chevron + destination card. Gated on
    // isFallback AND isHarness so it can never appear in the real game. The stand-in sits a
    // fixed distance down the road ahead of the truck so the chevron reads correctly.
    if (!at && gp.isFallback && this.ctx.isHarness) {
      at = this._previewTarget;
      const d = T.chevron.previewDist;
      const f = pl.forward;
      at.position.set(
        pl.position.x + f.x * d - f.z * T.chevron.previewLateral,
        pl.position.y + 0.4,
        pl.position.z + f.z * d + f.x * T.chevron.previewLateral,
      );
      at.distance = d;
    }

    const pos = at && (at.position || at.pos);
    if (!at || !pos) { t.valid = false; return; }

    t.valid = true;
    t.name = at.name || 'Objectif';
    t.distance = at.distance != null ? at.distance : pl.position.distanceTo(pos);

    // Project to design-space screen coordinates. Camera-space test first so a target
    // behind the camera does not project to a mirrored point.
    const cam = this.ctx.camera;
    this._v.set(pos.x, pos.y + 1.6, pos.z);
    this._v2.copy(this._v).applyMatrix4(cam.matrixWorldInverse);
    const behind = this._v2.z > -0.05;
    this._v.project(cam);

    let sx = (this._v.x * 0.5 + 0.5) * this.viewW;
    let sy = (1 - (this._v.y * 0.5 + 0.5)) * this.viewH;
    const cx = this.viewW * 0.5, cy = this.viewH * 0.5;
    const inset = T.chevron.edgeInset;
    const onScreen = !behind && sx > inset && sx < this.viewW - inset
      && sy > inset && sy < this.viewH - inset;

    if (!onScreen) {
      let dx = sx - cx, dy = sy - cy;
      if (behind) { dx = -dx; dy = -dy; }
      const len = Math.sqrt(dx * dx + dy * dy) || 1;
      dx /= len; dy /= len;
      const rx = this.viewW * 0.5 - inset, ry = this.viewH * 0.5 - inset;
      const k = Math.min(Math.abs(rx / (dx || 1e-4)), Math.abs(ry / (dy || 1e-4)));
      sx = cx + dx * k;
      sy = cy + dy * k;
      t.edgeAngle = Math.atan2(dy, dx);
    } else {
      t.edgeAngle = 0;
    }
    t.onScreen = onScreen;
    t.x = sx;
    // Lift the chevron off the objective so it floats above the truck rather than on it;
    // the closer the target, the higher it has to sit to clear the chassis.
    const lift = T.chevron.liftFar
      + (T.chevron.liftNear - T.chevron.liftFar)
      * clamp01(1 - (t.distance - T.chevron.nearDist) / (T.chevron.farDist - T.chevron.nearDist));
    t.y = onScreen ? sy - lift : sy;

    // reference/REFERENCE.md §2.6 — the chevron hovers over the road AHEAD, near the top of
    // the frame, not on the objective. x keeps tracking the target (that is the whole
    // information content); y is clamped into the reference band so the arrow can never park
    // on the truck, and x is kept off the two corner columns.
    if (onScreen) {
      const k = T.chevron;
      if (t.y > k.bandBottom) t.y = k.bandBottom;
      if (t.y < k.bandTop) t.y = k.bandTop;
      t.x = clamp(t.x, k.safeLeft, this.viewW - k.safeRight);
    }
  }

  _syncResult(m) {
    const r = m.result;
    const fr = this.forced.result;
    if (fr) {
      if (fr.rank) r.rank = String(fr.rank).toUpperCase();
      if (fr.perfects != null) r.perfects = fr.perfects;
      if (fr.bestCombo != null) r.bestCombo = fr.bestCombo;
      if (fr.timeBonus != null) r.timeBonus = fr.timeBonus;
      if (fr.score != null) r.score = fr.score;
      if (fr.cash != null) r.cash = fr.cash;
    }
    if (this.forced.score !== undefined) r.score = this.forced.score;
    if (this.forced.cash !== undefined) r.cash = this.forced.cash;
    if (this.forced.repaired !== undefined) r.repaired = this.forced.repaired;
    if (this.forced.total !== undefined) r.total = this.forced.total;
    if (!r.total) r.total = m.total;
    if (!r.repaired) r.repaired = m.repaired;

    /**
     * ALLOCATION DISCIPLINE (ARCHITECTURE.md N9). These three strings were rebuilt on EVERY
     * frame of every shot and every second of play — for a panel that is only on screen
     * during `result`. They are now rebuilt only when one of their inputs actually moves.
     */
    const combo = r.bestCombo || m.combo || 0;
    const perfects = r.perfects || 0;
    const bonus = Math.round(r.timeBonus || 0);
    const c = this._chipSrc;
    if (c[0] !== combo) { c[0] = combo; m.chips[0].value = '×' + combo; }
    if (c[1] !== perfects) { c[1] = perfects; m.chips[1].value = String(perfects); }
    if (c[2] !== bonus) { c[2] = bonus; m.chips[2].value = '+' + bonus; }
  }

  // ------------------------------------------------------------------ drawing

  _draw(sim) {
    const m = this.model;
    const isResult = m.phase === 'result';
    const isTitle = m.phase === 'title';
    const isCountdown = m.phase === 'countdown';
    const isTimeUp = m.phase === 'timeup';
    const overlayActive = isResult || isTitle || isCountdown || isTimeUp || this._paused;
    const hudOn = m.visible && !isResult && !isTitle;

    // Expire timed items (presentation state only).
    const cl = this.callouts;
    for (let i = cl.count - 1; i >= 0; i--) {
      const it = cl.items[i];
      if (!it.sticky && sim - it.at > it.life) { cl.retire(i); this._forceRedraw = true; }
    }
    const bl = this.bonuses;
    for (let i = bl.count - 1; i >= 0; i--) {
      const it = bl.items[i];
      if (!it.sticky && sim - it.at > T.timeBonus.life) { bl.retire(i); this._forceRedraw = true; }
    }

    this._show('timer', hudOn);
    this._show('bonus', hudOn && bl.count > 0);
    this._show('plate', hudOn);
    this._show('callout', hudOn && cl.count > 0);
    this._show('card', hudOn && m.target.valid);
    this._show('drive', hudOn);
    this._show('meter', hudOn && (m.repair.active || m.repair.fade > 0.001));
    this._show('chevron', hudOn && m.target.valid);
    this._show('overlay', m.visible && overlayActive);

    this.scrim.style.display = (m.visible && (isResult || isTitle)) ? 'block' : 'none';
    if (isResult) this.scrim.style.background = C.cream;
    else if (isTitle) this.scrim.style.background = '#0B0D10';

    // When clocks are frozen every visible layer repaints on every call, so a shot is a
    // pure function of the final state rather than of the redraw history.
    const redraw = this._forceRedraw || this.frozen;

    if (hudOn) {
      // --- timer: on digit change, or every frame while it is pulsing
      const pulsing = m.timeRemaining <= T.timer.warnAt;
      const timerSig = pulsing ? sim : hashStr(fmtTime(Math.max(0, m.timeRemaining)));
      if (this._changed(0, timerSig) || redraw) drawTimer(this.layers.get('timer').g, m, sim);

      // --- "+N SEC" token, its own (small) layer
      if (bl.count > 0 && (this._changed(8, sim) || redraw)) {
        drawTimeBonuses(this.layers.get('bonus').g, bl, sim);
      }

      // --- money / score plate
      //
      // The signature is over the FORMATTED strings, not the raw odometer values. The board
      // is a letterboard: nothing on it can change until a character changes, so hashing
      // `cashShown` at cent precision made it repaint on all 60 frames of every roll-up for
      // maybe a dozen visible states. With per-cell glyph setting that repaint is no longer
      // cheap, and `ui.update` has a 500 µs p95 budget (ARCHITECTURE.md §9).
      const plateSig = hashStr(fmtMoney(m.cashShown)) + hashStr(fmtInt(m.scoreShown)) * 1e-4
        + m.repaired * 7919 + m.total * 104729 + m.multiplier * 13;
      if (this._changed(1, plateSig) || redraw) drawPlate(this.layers.get('plate').g, m, sim);

      // --- combo callouts (animating whenever any are live)
      if (cl.count > 0 && (this._changed(2, sim) || redraw)) {
        drawCallouts(this.layers.get('callout').g, cl, m, sim);
      }

      // --- destination card
      if (m.target.valid) {
        const cardSig = hashStr(m.target.name) + Math.round(m.target.distance);
        if (this._changed(3, cardSig) || redraw) drawCard(this.layers.get('card').g, m, sim);
      }

      // --- speed + gear
      const driveSig = Math.round(m.speedKmh) + (m.gear === 'R' ? 0.5 : 0);
      if (this._changed(4, driveSig) || redraw) drawDrive(this.layers.get('drive').g, m, sim);

      // --- repair meter
      if (m.repair.active || m.repair.fade > 0.001) {
        if (this._changed(5, sim) || redraw) drawMeter(this.layers.get('meter').g, m, sim);
      }

      // --- chevron. The bob is a transform, not a repaint: the canvas content depends only
      // on the quantised distance-scale, the on/off-screen flag and the edge angle.
      if (m.target.valid) {
        const k = T.chevron;
        const L = this.layers.get('chevron');
        const s = k.size;
        const bob = m.target.onScreen ? Math.sin(sim * Math.PI * 2 * k.bobHz) * k.bobPx : 0;
        L.el.style.transform =
          `translate(${(m.target.x - s * 0.5).toFixed(2)}px,${(m.target.y - s * 0.5 + bob).toFixed(2)}px)`;
        const d = clamp(m.target.distance, k.nearDist, k.farDist);
        const chevSig = Math.round(d / k.scaleQuantum) * 4
          + (m.target.onScreen ? 0 : 2 + Math.round(m.target.edgeAngle / k.angleQuantum) * 8);
        if (this._changed(6, chevSig) || redraw) drawChevron(L.g, m);
      }
    }

    // --- overlay
    if (m.visible && overlayActive) {
      const g = this.layers.get('overlay').g;
      let sig;
      if (this._paused) sig = 1e6;
      else if (isResult) { const age = sim - this._resultAt; sig = age < 2.1 ? Math.round(age * 40) : 2e6; }
      else if (isTitle) sig = Math.round((sim - this._titleAt) * 12);
      else if (isCountdown) sig = Math.round(m.countdown * 20);
      else sig = Math.round((sim - this._phaseAt) * 40);

      if (this._changed(7, sig) || redraw) {
        if (this._paused) drawPause(g, m, sim - this._pauseAt);
        else if (isResult) drawResult(g, m, sim - this._resultAt);
        else if (isTitle) drawTitle(g, m, sim - this._titleAt);
        else if (isCountdown) drawCountdown(g, m, sim - this._phaseAt);
        else drawTimeUp(g, m, sim - this._phaseAt);
      }
    }

    // --- vignettes (opacity only)
    const v = this._vigDamage;
    let dmg = 0;
    if (v.strength > 0) {
      const t = clamp01((sim - v.at) / v.decay);
      dmg = v.strength * (1 - t) * (1 - t);
      if (t >= 1) v.strength = 0;
    }
    setVignette(this.vigDamage, dmg);

    let clockV = 0;
    if (hudOn && m.timeRemaining <= T.timer.criticalAt && m.timeRemaining > 0) {
      const p = 0.5 + 0.5 * Math.sin(sim * Math.PI * 2 * T.vignette.criticalHz);
      clockV = T.vignette.criticalBase * (0.45 + 0.55 * p)
        * clamp01(1 - m.timeRemaining / T.timer.criticalAt);
    }
    setVignette(this.vigClock, clockV);

    this._forceRedraw = false;
  }

  _show(name, on) {
    const L = this.layers.get(name);
    if (!L) return;
    const d = on ? 'block' : 'none';
    if (L.el.style.display !== d) { L.el.style.display = d; if (on) this._forceRedraw = true; }
  }

  _changed(slot, value) {
    if (this._sig[slot] === value) return false;
    this._sig[slot] = value;
    return true;
  }

  // ------------------------------------------------------------------ harness

  /**
   * ARCHITECTURE.md §7.1. Forced values are STICKY until `reset()`: a shot asks for a
   * specific HUD state and must keep it across the settle/step passes that follow the
   * patch. Nothing calls this in normal play, so live gameplay values are used untouched.
   */
  applyHarnessState(patch) {
    if (!patch) return;
    const f = this.forced;
    const keys = ['hudVisible', 'timeRemaining', 'score', 'cash', 'combo', 'multiplier',
      'repaired', 'total', 'weather', 'activeTarget'];
    for (let i = 0; i < keys.length; i++) {
      const k = keys[i];
      if (patch[k] !== undefined) f[k] = patch[k];
    }

    // A forced total must be ON the plate in the shot, not part-way through its roll-up:
    // the harness sets state once and then steps, so land the odometer immediately.
    if (patch.cash !== undefined) snapOdo(this._odoCash, patch.cash);
    if (patch.score !== undefined) snapOdo(this._odoScore, patch.score);

    /**
     * CLEAR TRANSIENTS — lead integration pass, see workbench.md.
     *
     * The damage vignette is a full-viewport DOM layer at #E01E12 / 0.55 alpha. It decays
     * correctly in 0.62 s of sim time, but the harness capture driver scrapes scenery, and
     * every `vehicle:collided` / `pothole:thumped` re-arms it — so it was pinned ON in most
     * graded captures. `look` caught this by reading the raw WebGL framebuffer via
     * canvas.toDataURL and finding the CANVAS CLEAN (sky rgb(10,109,255), road neutral
     * rgb(140,144,147)) with the red gradient sitting above it in the DOM.
     *
     * Consequence: blind critics were grading a red overlay as our lighting — it turned the
     * road pink, the sky mauve, and inflated the measured shadow ratio on combo-hud to 0.795,
     * out of band, purely from the overlay.
     *
     * A graded shot should never carry incidental damage state. The harness clears it
     * immediately before the shutter.
     */
    if (patch.clearTransients) {
      this._vigDamage.strength = 0;
      this._vigDamage.at = -1e9;
      this.callouts.update((c) => !!c.sticky);
      this.bonuses.update((b) => !!b.sticky);
      this._forceRedraw = true;
    }

    if (patch.repairSession !== undefined) {
      f.repairSession = patch.repairSession;
      this._fillTo = -1; this._fillDone = false;   // re-latch the smoothing origin
      this._repairOnAt = this.ctx.time.sim - T.meter.fade;
      this._repairPinned = true;               // ...and keep that origin (see _syncRepair)
    }

    if (patch.phase !== undefined) {
      if (patch.phase !== this.model.phase) {
        this._phaseAt = this.ctx.time.sim;
        if (patch.phase === 'result') { this._resultAt = this.ctx.time.sim; this._latchPlace(); }
        if (patch.phase === 'title') this._titleAt = this.ctx.time.sim;
      }
      f.phase = patch.phase;
      this.model.phase = patch.phase;
    }

    if (patch.result !== undefined) f.result = patch.result;

    if (patch.announce !== undefined) this._applyAnnounce(patch);
    else if (patch.timeBonus !== undefined) this._pushBonus(patch.timeBonus, true);

    this._forceRedraw = true;
    this._sig.fill(NaN);
  }

  _applyAnnounce(patch) {
    const a = patch.announce;
    if (!a || !a.text) {
      for (let i = this.callouts.count - 1; i >= 0; i--) {
        if (this.callouts.items[i].sticky) this.callouts.retire(i);
      }
      return;
    }
    const txt = String(a.text).toUpperCase();
    const combo = patch.combo != null ? patch.combo : (this.forced.combo || 0);
    const mult = patch.multiplier != null ? patch.multiplier : (this.forced.multiplier || 1);
    let exists = false;
    for (let i = 0; i < this.callouts.count; i++) {
      const c = this.callouts.items[i];
      if (c.sticky && c.text === txt) { exists = true; c.combo = combo; c.mult = mult; }
    }
    if (!exists) {
      const it = this._spawnCallout();
      if (it) {
        it.text = txt;
        it.style = a.style || 'combo';
        it.at = this.ctx.time.sim;
        it.life = Infinity;
        it.sticky = true;
        it.combo = combo;
        it.mult = mult;
      }
    }
    // A scoring callout always coincides with a time award in the real game, so the shot
    // gets the "+N sec" popup with it. An explicit patch.timeBonus overrides the default.
    const style = a.style || 'combo';
    if (style === 'combo' || style === 'perfect' || style === 'repair') {
      let hasSticky = false;
      for (let i = 0; i < this.bonuses.count; i++) if (this.bonuses.items[i].sticky) hasSticky = true;
      if (!hasSticky) this._pushBonus(patch.timeBonus != null ? patch.timeBonus : 10, true);
    }
  }

  harnessStats() {
    const m = this.model;
    return {
      visible: m.visible,
      phase: m.phase,
      scale: +this.scale.toFixed(4),
      canvasScale: this.q,
      time: +Number(m.timeRemaining).toFixed(2),
      cashShown: +m.cashShown.toFixed(2),
      scoreShown: Math.round(m.scoreShown),
      combo: m.combo,
      multiplier: m.multiplier,
      repaired: m.repaired,
      total: m.total,
      callouts: this.callouts.count,
      bonuses: this.bonuses.count,
      meter: m.repair.active ? +m.repair.shown.toFixed(3) : null,
      target: m.target.valid ? m.target.name : null,
      rank: m.result.rank,
      layers: this.layers.size,
      frozen: this.frozen,
    };
  }

  /** ARCHITECTURE.md §7.3 — after this, pixels are a pure function of ctx.time.sim. */
  freezeClocks() {
    this.frozen = true;
    this._forceRedraw = true;
    this._sig.fill(NaN);
  }

  async reset(opts) {
    this.forced = Object.create(null);
    this.rng = this.ctx.rng.fork('ui');
    resetFamilies();
    // The inset raster is keyed by (name, weather, size) and is a pure function of them, so it
    // would survive a reset correctly — it is dropped anyway so a leaked canvas can never be
    // mistaken for `heapGrowthMB` that belongs to `ui` (§7.2b).
    resetThumbCache();
    this._resetRun();
    const m = this.model;
    m.phase = 'playing';
    m.repair.active = false;
    m.repair.shown = 0;
    m.repair.fade = 0;
    m.repair.snapAt = -1;
    m.target.valid = false;
    this._fillFrom = 0; this._fillTo = -1; this._fillAt = 0; this._fillDone = false;
    this._repairOnAt = -999; this._repairOffAt = -999;
    this._repairPinned = false;
    this._phaseAt = 0; this._resultAt = 0; this._titleAt = 0;
    this._paused = false; this._pausePrev = false;
    if (this._previewTarget) this._previewTarget.name = this.rng.pick(PREVIEW_LANDMARKS);
    m.seed = ((opts && opts.seed) != null ? opts.seed : this.ctx.seed) | 0 || 1;
    this._sig.fill(NaN);
    this._forceRedraw = true;
    const vp = this.ctx.viewport;
    this.onResize(vp.width || DES_W, vp.height || DES_H);
  }

  dispose() {
    for (let i = 0; i < this._offs.length; i++) {
      try { this._offs[i](); } catch { /* handler already gone */ }
    }
    this._offs.length = 0;
    if (this.host) { while (this.host.firstChild) this.host.removeChild(this.host.firstChild); }
    this.layers.clear();
    this.root = null;
  }
}

function snapOdo(o, v) {
  o.shown = v; o.from = v; o.to = v; o.at = -1e6;
}

/**
 * Full-screen gradient layers are expensive to composite, so a vignette at zero strength is
 * taken out of the layer tree entirely rather than left at opacity 0.
 */
function setVignette(el, v) {
  if (v <= 0.0015) {
    if (el.style.display !== 'none') el.style.display = 'none';
    return;
  }
  if (el.style.display !== 'block') el.style.display = 'block';
  el.style.opacity = v.toFixed(3);
}

/** Cheap stable string hash used for change detection. */
function hashStr(s) {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); }
  return h >>> 0;
}
